/**
 * AllNotifications
 *
 * Renders the primary list of notifications with support for marking as read/unread
 * and archiving individual items. Clicking a notification deep-links to the
 * appropriate dashboard tab and selection based on module and reference ID.
 */
import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import notificationsActionIcon from "../../../../assets/icons/notificationActionsIcon.svg";
import { getKeyResultById } from "../../../../api/initiative";
import { markAsArchived, markAsRead } from "../../../../api/notification";
import { getMilestoneById } from "../../../../api/milestones";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../constants/routes";

/** Generate 1–2 letter initials from a full name for avatar fallbacks. */
const getInitials = (fullName: string): string => {
  if (!fullName) return "";

  const names = fullName.trim().split(" ");
  if (names.length === 1) {
    return names[0].substring(0, 2).toUpperCase();
  }

  const firstName = names[0];
  const lastName = names.at(-1);
  return `${firstName?.charAt(0)}${lastName?.charAt(0)}`?.toUpperCase();
};

const ITEM_HEIGHT = 48;
interface Notification {
  id: number;
  user: string;
  message: string;
  detail: string;
  time: string;
  icon: string;
  referenceTitle?: string;
  isRead?: boolean;
  respectiveId?: number;
  moduleName?: string;
  moduleId?: number;
}

/** Props for the AllNotifications list component. */
const AllNotifications: React.FC<{
  data: Notification[];
  fetchNotifications: () => void;
  onClose: () => void;
}> = ({ data, fetchNotifications, onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>(data);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setNotifications(data);
  }, [data]);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    notification: Notification,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleMarkAsReadToggle = async () => {
    if (selectedNotification) {
      try {
        const newIsRead = !selectedNotification.isRead;
        await markAsRead(selectedNotification.id, newIsRead);
        fetchNotifications();
      } catch (error) {
        console.error(error);
      }
    }
    handleMenuClose();
  };

  const handleArchive = async () => {
    if (selectedNotification) {
      try {
        if (!selectedNotification.isRead) {
          await markAsRead(selectedNotification.id, true);
        }
        await markAsArchived(selectedNotification.id);
        fetchNotifications();
      } catch (error) {
        console.error(error);
      }
    }
    handleMenuClose();
  };

  const handleNotificationClick = async (item: Notification) => {// NOSONAR    
    if (!item.isRead) {
      try {
        await markAsRead(item.id, true);
        fetchNotifications();
      } catch (error) {
        console.error(error);
      }
    }
    const moduleName = item?.moduleName?.trim().toLowerCase();
    const moduleId = item?.moduleId;

    switch (moduleName) {
      case "objectives":
        if (moduleId === 1) {
          onClose();
          const queryParams = new URLSearchParams();
          queryParams.set("dashboardTab", "Objectives");
          queryParams.set("objectiveId", String(item.respectiveId));
          navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
        }
        break;

      case "key results":
        if (moduleId === 2) {
          try {
            const response = await getKeyResultById(item.respectiveId);
            if (
              response.data.success &&
              response?.data?.data?.objectives?.length > 0
            ) {
              const objectiveId = response.data.data.objectives[0].objectiveId;
              onClose();
              const queryParams = new URLSearchParams();
              queryParams.set("dashboardTab", "Objectives");
              queryParams.set("objectiveId", String(objectiveId));
              queryParams.set("keyresultId", String(item.respectiveId));
              navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
            }
          } catch (error) {
            console.error(error);
          }
        }
        break;

      case "initiatives":
        if (moduleId === 3) {
          onClose();
          const queryParams = new URLSearchParams();
          queryParams.set("dashboardTab", "Initiatives");
          queryParams.set("initiativeId", String(item.respectiveId));
          navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
        }
        break;

      case "milestone":
        if (moduleId === 4) {
          try {
            const response = await getMilestoneById(item.respectiveId);
            if (
              response.data.success &&
              response.data.data.linkedObjectivesAndKeyResults?.length > 0 &&
              response.data.data.initiative
            ) {
              const initiativeId = response.data.data.initiative.initiativeId;
              onClose();
              onClose();
              const queryParams = new URLSearchParams();
              queryParams.set("dashboardTab", "Initiatives");
              queryParams.set("initiativeId", String(initiativeId));
              navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
              //     initiativeId,
              //     objectiveId,
              //     keyResultId,
              //   })
              // );
            }
          } catch (error) {
            console.error(error);
          }
        }
        break;

      case "employee management":
        if (moduleId === 5) {
          onClose();
          navigate(routes.ROLESDASHBOARD);
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      {anchorEl && <div className="menu-overlay"></div>}
      <List>
        {notifications.length === 0 ? (
          <Typography
            sx={{
              textAlign: "center",
              color: "#696F8C",
              marginTop: "20px",
              fontFamily: "Manrope, sans-serif",
              fontSize: "13px",
              fontWeight: "400",
            }}
          >
            No notifications yet. You’re all caught up!
          </Typography>
        ) : (
          notifications.map((item) => (
            <ListItem
              key={item.id}
              alignItems="flex-start"
              sx={{
                borderBottom: "1px solid #eee",
                backgroundColor: item.isRead ? "#F9F9F9" : "#FFFFFF",
                "&:last-child": {
                  borderBottom: "none",
                },
                borderRadius: "12px",
                cursor: "pointer",
              }}
              onClick={() => handleNotificationClick(item)}
              secondaryAction={
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuClick(e, item);
                  }}
                >
                  <img
                    src={notificationsActionIcon}
                    alt="Add"
                    className="notification-action-icon"
                    style={{ cursor: "pointer" }}
                  />
                </IconButton>
              }
            >
              {/* Avatar */}
              <ListItemAvatar>
                <Avatar
                  src={item.icon}
                  alt={item.user}
                  sx={{
                    backgroundColor: item.icon ? "transparent" : "#403c6f",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {!item.icon && getInitials(item.user)}
                </Avatar>
              </ListItemAvatar>

              {/* Content */}
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle1"
                    fontWeight="600 !important"
                    color="#403C6F"
                    fontSize="14px"
                    fontFamily="Manrope, sans-serif"
                  >
                    {item.user}
                  </Typography>
                }
                secondary={
                  <Box>
                    {/* Message */}
                    <Typography
                      variant="body2"
                      color="#696F8C"
                      fontWeight="400 !important"
                      fontSize="12px"
                      fontFamily="Manrope, sans-serif"
                    >
                      {item.detail}{" "}
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        {item.referenceTitle}
                      </Box>
                    </Typography>

                    {/* Detail with purple bar */}
                    <Box
                      sx={{ display: "flex", alignItems: "stretch", mt: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: "2px",
                          bgcolor: "#6C4ED2",
                          borderRadius: 1,
                          mr: 1,
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="#696F8C"
                        fontWeight="400 !important"
                        fontSize="12px"
                        fontFamily="Manrope, sans-serif"
                        width="calc(100% - 20px)"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {item.message}
                      </Typography>
                    </Box>
                    {/* Time */}
                    <Typography
                      variant="caption"
                      color="#696F8C"
                      fontWeight="400 !important"
                      fontSize="12px"
                      fontFamily="Manrope, sans-serif"
                    >
                      {item.time}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))
        )}
      </List>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        container={document.body}
        disablePortal={false}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            elevation: 1,
            style: {
              maxHeight: ITEM_HEIGHT * 4.5,
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
              borderRadius: 16,
            },
            sx: {
              "&.MuiPaper-root": {
                padding: "0px !important",
                width: "156px",
                "--Paper-shadow": "none !important",
                "--Paper-elevation": "0 !important",
              },
            },
          },
          list: { "aria-labelledby": "long-button" },
        }}
      >
        <MenuItem
          onClick={() => {
            handleMarkAsReadToggle();
          }}
          sx={{ padding: "8px 16px !important" }}
        >
          {selectedNotification?.isRead ? "Mark as Unread" : "Mark as Read"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleArchive();
          }}
          sx={{ padding: "8px 16px !important" }}
        >
          Archive
        </MenuItem>
      </Menu>
    </>
  );
};

export default AllNotifications;
